import { isAfter } from "date-fns";
import { type z, type ZodIssue } from "zod";

import {
  Prisma,
  AnnualIncome,
  CreditScoreRange,
  MostRecentEviction,
  type File,
} from "@/.zenstack/logical-prisma-client";
import {
  type ListingEditorType,
  ListingEditorStep,
  ListingEditorStepMetadata,
  getStepDisplayName,
  type ListingValidationType,
  type ValidationRule,
  type ValidationSeverity,
  type FieldMapping,
  type FieldPath,
  StepValidationSchemas,
} from "@/components/listing-editor/listing-editor-steps";
import * as ValidationSchemas from "@/lib/validation/schemas";

// Note: Cross-form validation rules are now handled by CrossValidationErrorSchema
// in cross-validation-enhanced.schema.ts, following the dual-schema pattern

// Utility functions for type-safe validation
export function createValidationRule<T = ListingValidationType>(
  rule: ValidationRule<T>,
): ValidationRule<T> {
  return rule;
}

// Helper function to add warnings using Zod v4's superRefine
export function addWarningToSchema<T extends z.ZodTypeAny>(
  schema: T,
  warningRule: ValidationRule<ListingValidationType>,
  dataExtractor: (data: z.infer<T>) => ListingValidationType,
): T {
  return schema.superRefine((data, ctx) => {
    if (warningRule.validate && warningRule.severity === "warning") {
      const extractedData = dataExtractor(data);
      if (!warningRule.validate(extractedData)) {
        // For array fieldMapping, use the first mapping's fields for the path
        // (this helper is designed for single-form warnings, not cross-form)
        const fields = warningRule.fieldMapping?.[0]?.fields || [];
        ctx.addIssue({
          code: "custom",
          message: warningRule.message,
          severity: "warning",
          path: fields,
        });
      }
    }
  }) as T;
}

// Enhanced validation result that separates errors from warnings
export interface EnhancedValidationResult {
  success: boolean;
  errors: Array<{ message: string; path: (string | number)[] }>;
  warnings: Array<{ message: string; path: (string | number)[] }>;
}

// Helper function to parse Zod results and separate errors from warnings
export function parseZodResult(result: any): EnhancedValidationResult {
  if (result.success) {
    return { success: true, errors: [], warnings: [] };
  }

  const errors: Array<{ message: string; path: (string | number)[] }> = [];
  const warnings: Array<{ message: string; path: (string | number)[] }> = [];

  result.error.issues.forEach((issue: any) => {
    const issueData = {
      message: issue.message,
      path: issue.path,
    };

    if (issue.severity === "warning") {
      warnings.push(issueData);
    } else {
      errors.push(issueData);
    }
  });

  return {
    success: errors.length === 0, // Success if no errors (warnings don't block)
    errors,
    warnings,
  };
}

// Helper function to extract field data based on fieldMapping
export function extractFieldData<T, P extends FieldPath<T>>(
  data: T,
  fieldPath: P,
): any {
  const pathParts = fieldPath.split(".");
  let current: any = data;

  for (const part of pathParts) {
    if (current && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
}

// Helper function to validate specific fields from a listing
export function validateFields<T>(
  data: T,
  fieldMapping: FieldMapping<T>,
  validationFn: (fieldData: T) => boolean,
): boolean {
  return validationFn(data);
}

/**
 * Maps step names and issue paths to actual object field paths
 * Uses the formFields from ListingEditorStepMetadata to map validation issues to proper field paths
 */
export function getFieldPathFromStepAndIssue(
  step: ListingEditorStep,
  issuePath: (string | number)[],
): string {
  const metadata = ListingEditorStepMetadata[step];

  if (!metadata) {
    // Fallback: construct path from step and issue path
    return `listing.${step}.${issuePath.join(".")}`;
  }

  // Convert issue path to string for matching
  const pathSegments = issuePath.map((segment) => String(segment));

  // For steps with specific form fields, try to match the issue path to a form field
  if (metadata.formFields && metadata.formFields.length > 0) {
    // Handle nested paths by checking if any form field starts with the issue path
    for (const formField of metadata.formFields) {
      const formFieldSegments = formField.split(".");

      // Check if the issue path matches the beginning of this form field
      if (pathSegments.length <= formFieldSegments.length) {
        let matches = true;
        for (let i = 0; i < pathSegments.length; i++) {
          if (pathSegments[i] !== formFieldSegments[i]) {
            matches = false;
            break;
          }
        }

        if (matches) {
          // Return the full form field path with "listing." prefix
          return `listing.${formField}`;
        }
      }
    }

    // If no exact match found, try to find the closest match
    // For simple field names (single segment), match against the last segment of form fields
    if (pathSegments.length === 1) {
      for (const formField of metadata.formFields) {
        const formFieldSegments = formField.split(".");
        const lastSegment = formFieldSegments[formFieldSegments.length - 1];

        if (pathSegments[0] === lastSegment) {
          return `listing.${formField}`;
        }
      }
    }
  }

  // Fallback: construct path from step and issue path
  return `listing.${step}.${issuePath.join(".")}`;
}

// Example usage of the new Zod v4 warning-enabled validation system:
/*
// 1. Create a schema with integrated warnings using Zod v4's superRefine
const titleSchema = z.string()
  .min(5, "Title must be at least 5 characters") // Error
  .max(100, "Title must be less than 100 characters") // Error
  .superRefine((val, ctx) => {
    // Warning: Title could be more descriptive
    if (val.length < 20) {
      ctx.addIssue({
        code: "custom",
        message: "Consider adding more descriptive details to your title",
        severity: "warning",
        path: ["title"]
      });
    }
  });

// 2. Use the enhanced validation result parser
const result = titleSchema.safeParse("Short");
const validation = parseZodResult(result);
console.log("Success:", validation.success); // false (due to error)
console.log("Errors:", validation.errors); // ["Title must be at least 5 characters"]
console.log("Warnings:", validation.warnings); // ["Consider adding more descriptive details..."]

// 3. Create a comprehensive listing validation schema with warnings
const listingSchema = z.object({
  title: titleSchema,
  description: z.record(z.string(), z.unknown()).nullable()
    .superRefine((val, ctx) => {
      if (!val || Object.keys(val).length === 0) {
        ctx.addIssue({
          code: "custom",
          message: "Description is empty - consider adding property details",
          severity: "warning",
          path: ["description"]
        });
      }
    }),
  files: z.array(z.any())
    .min(1, "At least one file is required") // Error
    .superRefine((files, ctx) => {
      if (files.length < 5) {
        ctx.addIssue({
          code: "custom",
          message: "Consider adding more photos (5+ recommended)",
          severity: "warning",
          path: ["files"]
        });
      }
    })
});

// 4. Validate a listing with integrated error and warning handling
const listingData = {
  title: "Good Title",
  description: {},
  files: ["photo1.jpg"]
};

const listingResult = listingSchema.safeParse(listingData);
const listingValidation = parseZodResult(listingResult);

// Results:
// - Success: false (due to description error)
// - Errors: ["Description is required", "At least one file is required"]
// - Warnings: ["Consider adding more photos (5+ recommended)"]

// 5. Using the addWarningToSchema helper for reusable warning rules
const addressSchema = addWarningToSchema(
  AddressSchema,
  ValidationRules.address.zipCodeFormat,
  (data) => ({ details: { address: data } })
);
*/

// Define types using Prisma validators to ensure structural connection to the database schema
// Only use Prisma validators for models that exist in the Prisma client
const _auctionFormatArgs = Prisma.validator<Prisma.AuctionFormatDefaultArgs>()({
  select: {
    id: true,
    auctionStartDate: true,
    auctionEndDate: true,
    startingBid: true,
    reserveBidAmount: true,
    bidIncrement: true,
  },
});

const _forLeaseArgs = Prisma.validator<Prisma.ForLeaseDefaultArgs>()({
  select: {
    id: true,
    availableFrom: true,
    leaseTermMonths: true,
    listingId: true,
  },
});

const _tenantScreeningCriteriaArgs =
  Prisma.validator<Prisma.TenantScreeningCriteriaDefaultArgs>()({
    select: {
      isEnabled: true,
      creditScoreRange: true,
      annualIncome: true,
      mostRecentEviction: true,
      leaseTermsId: true,
    },
  });

// Export types that are structurally connected to the database schema
export type AuctionFormatType = Prisma.AuctionFormatGetPayload<
  typeof _auctionFormatArgs
>;
export type ForLeaseType = Prisma.ForLeaseGetPayload<typeof _forLeaseArgs>;
export type TenantScreeningCriteriaType =
  Prisma.TenantScreeningCriteriaGetPayload<typeof _tenantScreeningCriteriaArgs>;

/**
 * Centralized validation rules and messages for listing state transitions
 * Using proper types from generated Zod schemas with enhanced error/warning support
 */
/**
 * ValidationRules - Contains ONLY cross-form validations and business rule warnings
 *
 * DRY PRINCIPLE: Base field validations (required, min/max, format) are defined in:
 * - .zmodel files (source of truth) → generate Zod schemas
 * - Enhanced schemas add UX warnings on top of base validation
 * - Individual forms handle complex inline validation when needed
 *
 * This object should contain ONLY:
 * 1. Cross-form rules (validations spanning multiple steps)
 * 2. Business recommendations (UX warnings not enforced by schema)
 */
const ValidationRules = {
  // Cross-field validation rules (span multiple forms)
  crossField: {
    auctionLeaseConflict: {
      validate: (data: {
        atAuction?: AuctionFormatType | null;
        forLease?: ForLeaseType | null;
      }) => {
        if (!data.atAuction || !data.forLease) return true;
        if (data.atAuction.auctionEndDate && data.forLease.availableFrom) {
          return isAfter(
            data.forLease.availableFrom,
            data.atAuction.auctionEndDate,
          );
        }
        return true;
      },
      message: "Auction must end before the lease availability date.",
      severity: "error" as ValidationSeverity,
      fieldMapping: [
        {
          step: ListingEditorStep.AUCTION,
          fields: ["atAuction.auctionEndDate"],
        },
        {
          step: ListingEditorStep.FOR_LEASE,
          fields: ["forLease.availableFrom"],
        },
      ],
    },
  },
};

/**
 * Section-specific validation schemas for granular error reporting
 *
 * DRY PRINCIPLE: All schemas are imported from *-enhanced.schema.ts files.
 * This is the single source of truth - all validation logic lives in Enhanced Schemas.
 *
 * Note: These are primarily used by validateListingSection() for step-specific validation
 */
export const ListingSectionValidationSchemas = {
  // Core listing information - uses DescriptionErrorSchema from description-enhanced.schema
  core: ValidationSchemas.DescriptionErrorSchema,

  // Location/Address validation - uses LocationErrorSchema from location-enhanced.schema
  location: ValidationSchemas.LocationErrorSchema,

  // Property information validation - uses PropertyInfoErrorSchema from property-info-enhanced.schema
  // Note: This schema expects bedrooms/bathrooms as strings (form input format)
  "property-info": ValidationSchemas.PropertyInfoErrorSchema.pick({
    bedrooms: true,
    bathrooms: true,
    squareFootage: true,
  }),

  // Media validation - uses MediaErrorSchema from media-enhanced.schema
  media: ValidationSchemas.MediaErrorSchema.shape.files,

  // Amenities validation - uses AmenitiesErrorSchema from amenities-enhanced.schema
  amenities: ValidationSchemas.AmenitiesErrorSchema.shape.amenities,

  // Description validation - uses DescriptionErrorSchema from description-enhanced.schema
  description: ValidationSchemas.DescriptionErrorSchema.shape.description,

  // Auction details validation - uses AuctionSchedulingErrorSchema from scheduling-enhanced.schema
  "auction-details": ValidationSchemas.AuctionSchedulingErrorSchema,

  // Lease details validation - uses LeaseSchedulingErrorSchema from scheduling-enhanced.schema
  "lease-details": ValidationSchemas.LeaseSchedulingErrorSchema,

  // Cross-validation warnings - uses CrossValidationWarningSchema from cross-validation-enhanced.schema
  // Note: This is for listing-wide business rules that don't fit a single form
  summary: ValidationSchemas.CrossValidationWarningSchema,
};

/**
 * Enhanced validation result interface for structured error and warning reporting
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
  warnings: Record<string, string[]>;
  sectionErrors: Record<string, number>;
  sectionWarnings: Record<string, number>;
  // Enhanced field-level and step-level validation details
  fieldErrors: Record<string, ValidationIssue[]>;
  fieldWarnings: Record<string, ValidationIssue[]>;
  stepErrors: Record<string, ValidationIssue[]>;
  stepWarnings: Record<string, ValidationIssue[]>;
}

/**
 * Zod-compatible issue interface for errors and warnings
 */
export interface ValidationIssue {
  code: string;
  message: string;
  path: (string | number)[];
  severity: ValidationSeverity;
  fieldMapping?: FieldMapping;
}

/**
 * Validates a listing for transition to a specific status using step-specific schemas and cross-form validations
 * @param listing The listing to validate
 * @param files Array of files associated with the listing
 * @returns ValidationResult with errors and warnings
 */
export function validateListingForTransition(
  listing: ListingEditorType | undefined,
  files: File[] = [],
): ValidationResult {
  if (!listing) {
    return {
      isValid: false,
      errors: { general: ["Listing is required."] },
      warnings: {},
      sectionErrors: {},
      sectionWarnings: {},
      fieldErrors: {},
      fieldWarnings: {},
      stepErrors: {},
      stepWarnings: {},
    };
  }

  // Prepare validation data with defaults to prevent null issues
  // Note: We spread listing first to include all Prisma-generated fields like business
  const validationData: ListingValidationType = {
    ...listing,
    files: files || [],
  } as ListingValidationType;

  const errors: Record<string, string[]> = {};
  const warnings: Record<string, string[]> = {};
  const fieldErrors: Record<string, ValidationIssue[]> = {};
  const fieldWarnings: Record<string, ValidationIssue[]> = {};
  const stepErrors: Record<string, ValidationIssue[]> = {};
  const stepWarnings: Record<string, ValidationIssue[]> = {};
  const sectionErrors: Record<string, number> = {};
  const sectionWarnings: Record<string, number> = {};

  // Step 1: Validate each step using its specific Zod schema (ERRORS only)
  Object.entries(StepValidationSchemas).forEach(([step, schema]) => {
    try {
      let stepData: any;

      // Prepare data for each step
      switch (step as ListingEditorStep) {
        case ListingEditorStep.LOCATION:
          stepData = validationData.details?.address;
          break;
        case ListingEditorStep.PROPERTY_INFO:
          stepData = {
            bedrooms: validationData.details?.housingDetails?.bedrooms,
            bathrooms: validationData.details?.housingDetails?.bathrooms,
            squareFootage:
              validationData.details?.housingDetails?.squareFootage,
          };
          break;
        case ListingEditorStep.AMENITIES:
          stepData = {
            amenities: validationData.details?.housingDetails?.amenities || [],
          };
          break;
        case ListingEditorStep.MEDIA:
          stepData = { files: validationData.files };
          break;
        case ListingEditorStep.DESCRIPTION:
          stepData = {
            title: validationData.title,
            description: validationData.description,
          };
          break;
        case ListingEditorStep.AUCTION:
          stepData = validationData.atAuction
            ? {
                auctionStartDate: validationData.atAuction.auctionStartDate,
                auctionEndDate: validationData.atAuction.auctionEndDate,
                startingBid: validationData.atAuction.startingBid,
                reserveBidAmount: validationData.atAuction.reserveBidAmount,
                bidIncrement: validationData.atAuction.bidIncrement,
              }
            : undefined;
          break;
        case ListingEditorStep.FOR_LEASE:
          // If forLease is null/undefined, pass undefined and let the standard
          // missing object handling below (lines 573-607) create the error
          stepData = validationData.forLease
            ? {
                availableFrom: validationData.forLease.availableFrom,
                leaseTermMonths: validationData.forLease.leaseTermMonths,
              }
            : undefined;
          break;
        case ListingEditorStep.SUMMARY:
          // For cross-validation errors, pass only the fields needed for cross-form validation
          stepData = {
            atAuction: validationData.atAuction
              ? {
                  auctionEndDate: validationData.atAuction.auctionEndDate,
                }
              : null,
            forLease: validationData.forLease
              ? {
                  availableFrom: validationData.forLease.availableFrom,
                }
              : null,
          };
          break;
      }

      const result = schema.safeParse(stepData);
      if (!result.success) {
        // Initialize step errors array if it doesn't exist
        if (!stepErrors[step]) {
          stepErrors[step] = [];
        }

        // Check if this is a "missing object" error (entire step is null/undefined)
        // Note: Zod stores the actual received value (null/undefined), not strings
        const isMissingObjectError = result.error.issues.some(
          (issue) =>
            issue.code === "invalid_type" &&
            ((issue as any).received === null ||
              (issue as any).received === undefined) &&
            (issue as any).expected === "object",
        );

        if (isMissingObjectError) {
          // Create custom form-level messages for specific steps
          let customMessage: string;
          if (step === ListingEditorStep.AUCTION) {
            customMessage =
              "Auction details are required to create an auction listing.";
          } else if (step === ListingEditorStep.MEDIA) {
            customMessage = "At least one photo is required for your listing.";
          } else if (step === ListingEditorStep.FOR_LEASE) {
            customMessage =
              "Lease details are required to create a lease listing.";
          } else {
            customMessage = `${getStepDisplayName(step as ListingEditorStep)} information is required.`;
          }

          const severity =
            step === ListingEditorStep.MEDIA ||
            step === ListingEditorStep.AUCTION ||
            step === ListingEditorStep.FOR_LEASE
              ? "error"
              : "warning";

          const formLevelError: ValidationIssue = {
            code: "missing_step",
            message: customMessage,
            path: [],
            severity,
          };
          stepErrors[step].push(formLevelError);

          // Update sectionErrors or sectionWarnings count for the summary header
          if (severity === "error") {
            if (!sectionErrors[step]) {
              sectionErrors[step] = 0;
            }
            sectionErrors[step]++;
          } else {
            if (!sectionWarnings[step]) {
              sectionWarnings[step] = 0;
            }
            sectionWarnings[step]++;
          }

          // IMPORTANT: Skip processing ALL raw Zod errors when we have a missing object
          // This prevents "Invalid input: expected object, received undefined" from appearing
          // alongside the friendly message. We use continue/return here to exit early.
          // Don't process any issues from result.error.issues - we've handled it with our custom message
        } else {
          // Handle individual field errors (only when NOT a missing object error)
          result.error.issues.forEach((issue) => {
            const path = `${step}.${issue.path.join(".")}`;

            // Add to legacy errors format for backward compatibility
            if (!errors[path]) {
              errors[path] = [];
            }
            errors[path].push(issue.message);

            // Create ValidationIssue object
            const validationIssue: ValidationIssue = {
              code: issue.code,
              message: issue.message,
              path: issue.path as (string | number)[],
              severity:
                (issue as ZodIssue & { severity?: ValidationSeverity })
                  .severity || "error",
            };

            // Add to step-level errors only for issues with empty paths (form-level errors)
            if (issue.path.length === 0 && stepErrors[step]) {
              stepErrors[step].push(validationIssue);
            }

            // Add to field-level errors only for issues with actual field paths
            if (issue.path.length > 0) {
              const fieldPath = getFieldPathFromStepAndIssue(
                step as ListingEditorStep,
                issue.path as (string | number)[],
              );
              if (!fieldErrors[fieldPath]) {
                fieldErrors[fieldPath] = [];
              }
              fieldErrors[fieldPath].push(validationIssue);

              // Track section errors count for the step that generated the error
              if (!sectionErrors[step]) {
                sectionErrors[step] = 0;
              }
              sectionErrors[step]++;

              // For SUMMARY step (cross-validation), also increment sectionErrors
              // for the related step based on the field path
              if (step === ListingEditorStep.SUMMARY && issue.path.length > 0) {
                const firstPathSegment = issue.path[0] as string;
                // Map camelCase to kebab-case for step names
                let relatedStep: string | null = null;
                if (firstPathSegment === "forLease") {
                  relatedStep = ListingEditorStep.FOR_LEASE;
                } else if (firstPathSegment === "atAuction") {
                  relatedStep = ListingEditorStep.AUCTION;
                }

                if (relatedStep) {
                  // Initialize if undefined before incrementing
                  if (sectionErrors[relatedStep] === undefined) {
                    sectionErrors[relatedStep] = 0;
                  }
                  // TypeScript doesn't recognize the above check, so we use non-null assertion
                  sectionErrors[relatedStep]! = sectionErrors[relatedStep]! + 1;
                }
              }
            }
          });
        }
      }
    } catch (error) {
      console.warn(`Step validation failed for ${step}:`, error);
    }
  });

  // Step 1.5: Validate WARNING schemas for each step
  const warningSchemas: Partial<Record<ListingEditorStep, z.ZodSchema<any>>> = {
    [ListingEditorStep.LOCATION]: ValidationSchemas.LocationWarningSchema,
    [ListingEditorStep.PROPERTY_INFO]:
      ValidationSchemas.PropertyInfoWarningSchema,
    [ListingEditorStep.AMENITIES]: ValidationSchemas.AmenitiesWarningSchema,
    [ListingEditorStep.MEDIA]: ValidationSchemas.MediaWarningSchema,
    [ListingEditorStep.DESCRIPTION]: ValidationSchemas.DescriptionWarningSchema,
    [ListingEditorStep.AUCTION]:
      ValidationSchemas.AuctionSchedulingWarningSchema,
    [ListingEditorStep.FOR_LEASE]: ValidationSchemas.ScreeningWarningSchema,
    [ListingEditorStep.SUMMARY]: ValidationSchemas.CrossValidationWarningSchema,
  };

  Object.entries(warningSchemas).forEach(([step, warningSchema]) => {
    if (!warningSchema) return;

    try {
      let stepData: any;

      // Prepare data for each step (same as error validation)
      switch (step as ListingEditorStep) {
        case ListingEditorStep.LOCATION:
          stepData = validationData.details?.address;
          break;
        case ListingEditorStep.PROPERTY_INFO:
          stepData = {
            bedrooms: validationData.details?.housingDetails?.bedrooms,
            bathrooms: validationData.details?.housingDetails?.bathrooms,
            squareFootage:
              validationData.details?.housingDetails?.squareFootage,
          };
          break;
        case ListingEditorStep.AMENITIES:
          stepData = {
            amenities: validationData.details?.housingDetails?.amenities || [],
          };
          break;
        case ListingEditorStep.MEDIA:
          stepData = { files: validationData.files };
          break;
        case ListingEditorStep.DESCRIPTION:
          stepData = {
            title: validationData.title,
            description: validationData.description,
          };
          break;
        case ListingEditorStep.AUCTION:
          // Extract only the fields the schema expects (no Prisma metadata)
          stepData = validationData.atAuction
            ? {
                auctionStartDate: validationData.atAuction.auctionStartDate,
                auctionEndDate: validationData.atAuction.auctionEndDate,
                startingBid: validationData.atAuction.startingBid,
                reserveBidAmount: validationData.atAuction.reserveBidAmount,
                bidIncrement: validationData.atAuction.bidIncrement,
              }
            : undefined;
          break;
        case ListingEditorStep.FOR_LEASE:
          // For screening warnings, we need both forLease and screening criteria data
          if (validationData.forLease) {
            stepData = {
              availableFrom: validationData.forLease.availableFrom,
              leaseTermMonths: validationData.forLease.leaseTermMonths,
              isEnabled:
                validationData.forLease.tenantScreeningCriteria?.isEnabled ||
                false,
              creditScoreRange:
                validationData.forLease.tenantScreeningCriteria
                  ?.creditScoreRange || CreditScoreRange.NOT_REQUIRED,
              annualIncome:
                validationData.forLease.tenantScreeningCriteria?.annualIncome ||
                AnnualIncome.NOT_REQUIRED,
              mostRecentEviction:
                validationData.forLease.tenantScreeningCriteria
                  ?.mostRecentEviction || MostRecentEviction.NOT_REQUIRED,
            };
          } else {
            stepData = undefined;
          }
          break;
        case ListingEditorStep.SUMMARY:
          // For cross-validation warnings, pass only the fields needed for cross-form validation
          stepData = {
            atAuction: validationData.atAuction
              ? {
                  auctionEndDate: validationData.atAuction.auctionEndDate,
                }
              : null,
            forLease: validationData.forLease
              ? {
                  availableFrom: validationData.forLease.availableFrom,
                }
              : null,
          };
          break;
      }

      const result = warningSchema.safeParse(stepData);
      if (!result.success) {
        // Process warning issues
        result.error.issues.forEach((issue) => {
          // Only process issues with severity: "warning"
          const issueWithSeverity = issue as ZodIssue & { severity?: string };
          if (issueWithSeverity.severity !== "warning") return;

          const validationIssue: ValidationIssue = {
            code: issue.code,
            message: issue.message,
            path: issue.path as (string | number)[],
            severity: "warning",
          };

          // Add to step-level warnings for form-level warnings (empty path)
          if (issue.path.length === 0) {
            if (!stepWarnings[step]) {
              stepWarnings[step] = [];
            }
            stepWarnings[step].push(validationIssue);
          }

          // Add to field-level warnings for field-specific warnings
          if (issue.path.length > 0) {
            const fieldPath = getFieldPathFromStepAndIssue(
              step as ListingEditorStep,
              issue.path as (string | number)[],
            );
            if (!fieldWarnings[fieldPath]) {
              fieldWarnings[fieldPath] = [];
            }
            fieldWarnings[fieldPath].push(validationIssue);

            // Track section warnings count for the step that generated the warning
            if (!sectionWarnings[step]) {
              sectionWarnings[step] = 0;
            }
            sectionWarnings[step]++;

            // For SUMMARY step (cross-validation), also increment sectionWarnings
            // for the related step based on the field path
            if (step === ListingEditorStep.SUMMARY && issue.path.length > 0) {
              const firstPathSegment = issue.path[0] as string;
              // Map camelCase to kebab-case for step names
              // e.g., "forLease" -> "for-lease", "atAuction" -> "auction"
              let relatedStep: string | null = null;
              if (firstPathSegment === "forLease") {
                relatedStep = ListingEditorStep.FOR_LEASE;
              } else if (firstPathSegment === "atAuction") {
                relatedStep = ListingEditorStep.AUCTION;
              }

              if (relatedStep) {
                // Initialize if undefined before incrementing
                if (sectionWarnings[relatedStep] === undefined) {
                  sectionWarnings[relatedStep] = 0;
                }
                // TypeScript doesn't recognize the above check, so we use non-null assertion
                sectionWarnings[relatedStep]! =
                  sectionWarnings[relatedStep]! + 1;
              }
            }

            // Add to legacy warnings format using step name
            const warningKey = `${step}.${issue.path.join(".")}`;
            if (!warnings[warningKey]) {
              warnings[warningKey] = [];
            }
            warnings[warningKey].push(issue.message);
          }
        });
      }
    } catch (error) {
      console.warn(`Warning validation failed for ${step}:`, error);
    }
  });

  // Calculate isValid: must have no errors AND no stepErrors AND no sectionErrors
  // stepErrors contain form-level errors (like missing auction/lease objects)
  // sectionErrors contain aggregated error counts per section
  const hasErrors = Object.keys(errors).length > 0;
  const hasStepErrors = Object.values(stepErrors).some(
    (errors) => errors.length > 0,
  );
  const hasSectionErrors = Object.values(sectionErrors).some(
    (count) => count > 0,
  );

  return {
    isValid: !hasErrors && !hasStepErrors && !hasSectionErrors,
    errors,
    warnings,
    sectionErrors,
    sectionWarnings,
    fieldErrors,
    fieldWarnings,
    stepErrors,
    stepWarnings,
  };
}

/**

    // Run section-specific validations for granular error reporting
    Object.entries(ListingSectionValidationSchemas).forEach(
      ([sectionName, schema]) => {
        try {
          let sectionData: unknown;

          switch (sectionName) {
            case "core":
              sectionData = {
                title: listing?.title,
                description: listing?.description,
              };
              break;
            case "location":
              sectionData = listing?.details?.address;
              break;
            case "property-info":
              sectionData = listing?.details?.housingDetails;
              break;
            case "media":
              sectionData = files || [];
              break;
            case "amenities":
              sectionData = listing?.details?.housingDetails?.amenities;
              break;
            case "description":
              sectionData = listing?.description;
              break;
            case "auction-details":
              sectionData = listing?.atAuction;
              break;
            case "lease-details":
              sectionData = listing?.forLease;
              break;
            default:
              sectionData = null;
          }

          const sectionResult = schema.safeParse(sectionData);
          if (!sectionResult.success) {
            sectionErrors[sectionName] = sectionResult.error.issues.length;
          } else {
            sectionErrors[sectionName] = 0;
          }

          // Count warnings for this section
          const sectionWarningsCount = Object.keys(warnings).filter((key) =>
            key.startsWith(sectionName),
          ).length;
          sectionWarnings[sectionName] = sectionWarningsCount;
        } catch (_err) {
          sectionErrors[sectionName] = 1; // Generic error count
          sectionWarnings[sectionName] = 0;
        }
      },
    );

    return {
      isValid: result.success,
      errors,
      warnings,
      sectionErrors,
      sectionWarnings,
    };
  } catch (error) {
    console.error("Validation error:", error);
    return {
      isValid: false,
      errors: { general: ["Validation failed due to unexpected error"] },
      warnings: {},
      sectionErrors: Object.keys(ListingSectionValidationSchemas).reduce(
        (acc, key) => {
          acc[key] = 1;
          return acc;
        },
        {} as Record<string, number>,
      ),
      sectionWarnings: Object.keys(ListingSectionValidationSchemas).reduce(
        (acc, key) => {
          acc[key] = 0;
          return acc;
        },
        {} as Record<string, number>,
      ),
    };
  }
}

/**
 * Validates a specific section of the listing
 *
 * DRY PRINCIPLE: Uses zmodel-generated schemas which already contain validations.
 * Warnings are handled by Enhanced Schemas used in individual forms.
 *
 * @param sectionName - The section to validate
 * @param data - The data for that section
 * @returns Validation result for the specific section
 */
export function validateListingSection(
  sectionName: keyof typeof ListingSectionValidationSchemas,
  data: unknown,
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const schema = ListingSectionValidationSchemas[sectionName];
  if (!schema) {
    return { isValid: false, errors: ["Unknown section"], warnings: [] };
  }

  const result = schema.safeParse(data);

  // Separate errors from warnings (Zod v4 supports severity: "warning")
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!result.success) {
    result.error.issues.forEach((issue) => {
      const zodIssue = issue as z.ZodIssue & { severity?: ValidationSeverity };
      if (zodIssue.severity === "warning") {
        warnings.push(issue.message);
      } else {
        errors.push(issue.message);
      }
    });
  }

  return {
    isValid: result.success,
    errors,
    warnings,
  };
}

/**
 * Get validation issues for a specific step
 *
 * DRY PRINCIPLE: Validation is handled by step-specific schemas (Enhanced or zmodel-generated).
 * This function validates using the schema from StepValidationSchemas.
 *
 * @param step - The listing editor step
 * @param listing - The listing data
 * @param files - Associated files
 * @returns Array of validation issues for the step
 */
export function getValidationIssuesForStep(
  step: ListingEditorStep,
  listing: ListingEditorType | undefined,
  files: File[] = [],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const schema = StepValidationSchemas[step];

  if (!schema || !listing) {
    return issues;
  }

  // Prepare data for the step
  let stepData: unknown;
  switch (step) {
    case ListingEditorStep.LOCATION:
      stepData = listing.details?.address;
      break;
    case ListingEditorStep.PROPERTY_INFO:
      stepData = {
        bedrooms: listing.details?.housingDetails?.bedrooms,
        bathrooms: listing.details?.housingDetails?.bathrooms,
        squareFootage: listing.details?.housingDetails?.squareFootage,
      };
      break;
    case ListingEditorStep.AMENITIES:
      stepData = {
        amenities: listing.details?.housingDetails?.amenities || [],
      };
      break;
    case ListingEditorStep.MEDIA:
      stepData = { files };
      break;
    case ListingEditorStep.DESCRIPTION:
      stepData = {
        title: listing.title,
        description: listing.description,
      };
      break;
    case ListingEditorStep.AUCTION:
      stepData = listing.atAuction;
      break;
    case ListingEditorStep.FOR_LEASE:
      stepData = listing.forLease;
      break;
    case ListingEditorStep.SUMMARY:
      // SUMMARY step validations are handled differently:
      // - Cross-form ERRORS (e.g., auction/lease date conflicts) are in CrossValidationErrorSchema
      // - Cross-validation WARNINGS are in CrossValidationWarningSchema
      // Both are processed in validateListingForTransition, not here
      return issues;
  }

  const result = schema.safeParse(stepData);
  if (!result.success) {
    result.error.issues.forEach((issue) => {
      const zodIssue = issue as z.ZodIssue & { severity?: ValidationSeverity };
      issues.push({
        code: issue.code,
        message: issue.message,
        path: issue.path as (string | number)[],
        severity: zodIssue.severity || "error",
      });
    });
  }

  return issues;
}

/**
 * Get all validation rules for a specific step
 *
 * DRY PRINCIPLE: Returns only cross-form validation rules and business rules that apply to this step.
 * Base field validations are handled by schemas.
 *
 * @param step - The listing editor step
 * @returns Array of validation rules for the step
 */
export function getValidationRulesForStep(
  step: ListingEditorStep,
): ValidationRule[] {
  const rules: ValidationRule[] = [];

  // Add cross-form validation rules
  // Check if ANY mapping in the fieldMapping array matches the step
  Object.values(ValidationRules.crossField).forEach((rule) => {
    if (
      rule.fieldMapping &&
      rule.fieldMapping.some((mapping) => mapping.step === step)
    ) {
      rules.push(rule as ValidationRule);
    }
  });

  // Note: Business rule warnings are now handled by CrossValidationWarningSchema
  // and are automatically processed in the SUMMARY step warning validation

  return rules;
}

/**
 * Check if a step has any validation issues
 * @param step - The listing editor step
 * @param listing - The listing data
 * @param files - Associated files
 * @returns Object with error and warning counts
 */
export function getStepValidationStatus(
  step: ListingEditorStep,
  listing: ListingEditorType | undefined,
  files: File[] = [],
): {
  hasErrors: boolean;
  hasWarnings: boolean;
  errorCount: number;
  warningCount: number;
} {
  const issues = getValidationIssuesForStep(step, listing, files);

  const errors = issues.filter((issue) => issue.severity === "error");
  const warnings = issues.filter((issue) => issue.severity === "warning");

  return {
    hasErrors: errors.length > 0,
    hasWarnings: warnings.length > 0,
    errorCount: errors.length,
    warningCount: warnings.length,
  };
}

/**
 * Business rule validations that go beyond basic schema validation
 *
 * DRY PRINCIPLE: Uses cross-form ValidationRules and BusinessRuleWarnings
 * instead of duplicating validation logic.
 */
export const BusinessRuleValidations = {
  // Ensure auction dates don't conflict with lease availability
  auctionLeaseConflict: (
    auction: AuctionFormatType | null,
    forLease: ForLeaseType | null,
  ) => {
    return ValidationRules.crossField.auctionLeaseConflict.validate({
      atAuction: auction,
      forLease,
    });
  },

  // Ensure minimum media quality requirements (from zmodel-generated schema)
  mediaQuality: (files: File[]) => {
    return files && files.length >= 1; // Matches zmodel min requirement
  },
};

/**
 * Runs all business rule validations
 * @param listing - The listing object
 * @param files - Array of files
 * @returns Array of business rule violations with specific error messages
 */
export function validateBusinessRules(
  listing: ListingEditorType | undefined,
  _files: File[] = [],
): string[] {
  const violations: string[] = [];

  if (!listing) return violations;

  // Check auction-lease conflicts
  if (
    !ValidationRules.crossField.auctionLeaseConflict.validate({
      atAuction: listing.atAuction,
      forLease: listing.forLease,
    })
  ) {
    violations.push(ValidationRules.crossField.auctionLeaseConflict.message);
  }

  return violations;
}

/**
 * Export validation rules for external use
 * This allows other parts of the application to access validation logic and messages
 */
export { ValidationRules };

/**
 * Get validation rule by category and name
 * @param category - The validation category (e.g., 'core', 'address', 'auction')
 * @param ruleName - The specific rule name (e.g., 'title', 'required', 'endAfterStart')
 * @returns The validation rule object with validate function and message
 */
export function getValidationRule(
  category: keyof typeof ValidationRules,
  ruleName: string,
): { validate: (data: unknown) => boolean; message: string } | null {
  const categoryRules = ValidationRules[category] as Record<
    string,
    { validate: (data: unknown) => boolean; message: string }
  >;
  return categoryRules[ruleName] || null;
}

/**
 * Get all validation messages for a specific category
 * @param category - The validation category
 * @returns Array of all validation messages in that category
 */
export function getValidationMessages(
  category: keyof typeof ValidationRules,
): string[] {
  const categoryRules = ValidationRules[category] as Record<
    string,
    { message: string }
  >;
  return Object.values(categoryRules).map((rule) => rule.message);
}
